import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  ThemeProvider,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, createTheme } from "@mui/material/styles";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CloseIcon from "@mui/icons-material/Close";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { BlobWriter, ZipWriter } from "@zip.js/zip.js";

type UploadItem = {
  id: string;
  guestName: string;
  originalName: string;
  mimeType: string;
  fileSize: number;
  uploadedAt: string;
};

type UploadStats = {
  totalFiles: number;
  images: number;
  videos: number;
  other: number;
  totalBytes: number;
};

type UploadsResponse = {
  result: number;
  message?: string;
  stats?: UploadStats;
  uploads?: UploadItem[];
};

type FilterValue = "all" | "images" | "videos";

const galleryTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#C7A34A",
      light: "#E2C777",
      dark: "#8F6E20",
      contrastText: "#0B0B0C",
    },
    background: {
      default: "#09090A",
      paper: "#141415",
    },
    text: {
      primary: "#F4F1E8",
      secondary: "#B7B1A3",
    },
    divider: "rgba(199,163,74,0.24)",
    error: {
      main: "#C86B6B",
    },
  },
  shape: {
    borderRadius: 5,
  },
  typography: {
    fontFamily:
      '"Source Sans 3",-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Arial,sans-serif',
    h1: {
      fontFamily: '"Cormorant Garamond", Georgia, serif',
      fontWeight: 500,
    },
    button: {
      textTransform: "none",
      fontWeight: 600,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          backgroundColor: "#09090A",
        },
        body: {
          minHeight: "100vh",
          backgroundColor: "#09090A",
          backgroundImage: `
            radial-gradient(900px 500px at 10% -10%, rgba(199,163,74,0.10), transparent 62%),
            radial-gradient(700px 420px at 100% 20%, rgba(199,163,74,0.06), transparent 58%),
            linear-gradient(rgba(255,255,255,0.014) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.014) 1px, transparent 1px)
          `,
          backgroundSize: "auto, auto, 32px 32px, 32px 32px",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: "1px solid rgba(199,163,74,0.22)",
          background:
            "linear-gradient(180deg, rgba(199,163,74,0.05), rgba(20,20,21,0.96) 34%)",
          boxShadow: "0 16px 36px rgba(0,0,0,0.24)",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
          backgroundImage: `
            radial-gradient(900px 420px at -10% -10%, ${alpha(theme.palette.primary.main, 0.1)}, transparent 62%),
            linear-gradient(180deg, #171718 0%, #101011 100%)
          `,
        }),
      },
    },
  },
});

const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";

  const units = ["B", "KB", "MB", "GB", "TB"];
  const unitIndex = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );

  const value = bytes / 1024 ** unitIndex;
  const digits = unitIndex >= 3 ? 2 : unitIndex === 2 ? 1 : 0;

  return `${value.toFixed(digits)} ${units[unitIndex]}`;
};

const formatDate = (value: string): string => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("pl-PL", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const getToken = (): string =>
  new URLSearchParams(window.location.search).get("token")?.trim() ?? "";

const StatCard = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
}) => (
  <Card>
    <CardContent sx={{ p: 2.2, "&:last-child": { pb: 2.2 } }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 44,
            height: 44,
            flexShrink: 0,
            display: "grid",
            placeItems: "center",
            border: "1px solid rgba(199,163,74,.32)",
            color: "primary.light",
            backgroundColor: "rgba(199,163,74,.07)",
          }}
        >
          {icon}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              fontSize: "1.55rem",
              lineHeight: 1.05,
              color: "text.primary",
            }}
          >
            {value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);


const sanitizeArchivePart = (
  value: string,
  fallback: string,
): string => {
  const sanitized = value
    .normalize("NFKC")
    .replace(/[<>:"/\\|?*]/g, "_")
    .split("")
    .map((character) =>
      character.charCodeAt(0) < 32 ? "_" : character,
    )
    .join("")
    .replace(/\s+/g, " ")
    .replace(/^\.+/, "")
    .trim();

  return sanitized || fallback;
};

const createArchivePath = (
  item: UploadItem,
  usedPaths: Set<string>,
): string => {
  const folder = sanitizeArchivePart(
    item.guestName,
    "Tajemniczy Gość",
  );
  const originalName = sanitizeArchivePart(
    item.originalName,
    `plik-${item.id}`,
  );

  let candidate = `${folder}/${originalName}`;

  if (!usedPaths.has(candidate)) {
    usedPaths.add(candidate);
    return candidate;
  }

  const lastDot = originalName.lastIndexOf(".");
  const baseName =
    lastDot > 0 ? originalName.slice(0, lastDot) : originalName;
  const extension =
    lastDot > 0 ? originalName.slice(lastDot) : "";

  let suffix = 2;

  while (usedPaths.has(candidate)) {
    candidate = `${folder}/${baseName} (${suffix})${extension}`;
    suffix += 1;
  }

  usedPaths.add(candidate);
  return candidate;
};


type SaveFilePickerOptions = {
  suggestedName?: string;
  types?: Array<{
    description?: string;
    accept: Record<string, string[]>;
  }>;
};

type FileSystemFileHandleLike = {
  createWritable: () => Promise<WritableStream<Uint8Array>>;
};

type WindowWithSaveFilePicker = Window & {
  showSaveFilePicker?: (
    options?: SaveFilePickerOptions,
  ) => Promise<FileSystemFileHandleLike>;
};

const createCountedStream = (
  source: ReadableStream<Uint8Array>,
  completedBytesBeforeFile: number,
  totalBytes: number,
  onProgress: (progress: number) => void,
): ReadableStream<Uint8Array> => {
  let currentFileBytes = 0;

  return source.pipeThrough(
    new TransformStream<Uint8Array, Uint8Array>({
      transform(chunk, controller) {
        currentFileBytes += chunk.byteLength;

        const transferredBytes =
          completedBytesBeforeFile + currentFileBytes;

        onProgress(
          totalBytes > 0
            ? Math.min(
                99,
                Math.round(
                  (transferredBytes / totalBytes) * 100,
                ),
              )
            : 0,
        );

        controller.enqueue(chunk);
      },
    }),
  );
};

export default function CoupleGallery() {
  const token = React.useMemo(getToken, []);
  const [uploads, setUploads] = React.useState<UploadItem[]>([]);
  const [stats, setStats] = React.useState<UploadStats>({
    totalFiles: 0,
    images: 0,
    videos: 0,
    other: 0,
    totalBytes: 0,
  });
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState("");
  const [filter, setFilter] = React.useState<FilterValue>("all");
  const [previewItem, setPreviewItem] = React.useState<UploadItem | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState("");
  const [archivePreparing, setArchivePreparing] = React.useState(false);
  const [archiveProgress, setArchiveProgress] = React.useState(0);
  const [archiveCurrentFile, setArchiveCurrentFile] = React.useState("");
  const [archiveError, setArchiveError] = React.useState("");

  const loadUploads = React.useCallback(
    async (showRefreshState = false) => {
      if (!token) {
        setError("Brakuje tokenu dostępu w adresie strony.");
        setLoading(false);
        return;
      }

      showRefreshState ? setRefreshing(true) : setLoading(true);
      setError("");

      try {
        const response = await fetch(
          `/api/couple/uploads?token=${encodeURIComponent(token)}`,
          {
            headers: {
              Accept: "application/json",
            },
            cache: "no-store",
          },
        );

        const data = (await response.json()) as UploadsResponse;

        if (!response.ok || data.result !== 0) {
          throw new Error(data.message || "Nie udało się pobrać listy plików.");
        }

        setUploads(data.uploads ?? []);
        setStats(
          data.stats ?? {
            totalFiles: 0,
            images: 0,
            videos: 0,
            other: 0,
            totalBytes: 0,
          },
        );
      } catch (loadError) {
        console.error("Could not load couple gallery:", loadError);
        setError(
          loadError instanceof Error
            ? loadError.message
            : "Nie udało się pobrać listy plików.",
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [token],
  );

  React.useEffect(() => {
    void loadUploads();
  }, [loadUploads]);

  const filteredUploads = React.useMemo(() => {
    if (filter === "images") {
      return uploads.filter((item) => item.mimeType.startsWith("image/"));
    }

    if (filter === "videos") {
      return uploads.filter((item) => item.mimeType.startsWith("video/"));
    }

    return uploads;
  }, [filter, uploads]);

  const fileUrl = React.useCallback(
    (item: UploadItem) =>
      `/api/couple/file/${encodeURIComponent(item.id)}?token=${encodeURIComponent(token)}`,
    [token],
  );

  const downloadAllAsZip = async (): Promise<void> => {
    if (!uploads.length || archivePreparing) return;

    setArchivePreparing(true);
    setArchiveProgress(0);
    setArchiveCurrentFile("");
    setArchiveError("");

    const totalBytes = uploads.reduce(
      (sum, item) => sum + item.fileSize,
      0,
    );
    let completedBytes = 0;
    const usedPaths = new Set<string>();

    const saveFilePicker = (
      window as WindowWithSaveFilePicker
    ).showSaveFilePicker;

    let zipWriter: ZipWriter<unknown> | undefined;
    let blobWriter: BlobWriter | undefined;
    let saveDirectlyToDisk = false;

    try {
      if (saveFilePicker) {
        try {
          const fileHandle = await saveFilePicker({
            suggestedName: "Wrzutka-weselna.zip",
            types: [
              {
                description: "Archiwum ZIP",
                accept: {
                  "application/zip": [".zip"],
                },
              },
            ],
          });

          const writable = await fileHandle.createWritable();

          zipWriter = new ZipWriter(writable, {
            zip64: true,
          });
          saveDirectlyToDisk = true;
        } catch (pickerError) {
          if (
            pickerError instanceof DOMException &&
            pickerError.name === "AbortError"
          ) {
            return;
          }

          console.warn(
            "Direct file saving is unavailable, using Blob fallback:",
            pickerError,
          );
        }
      }

      if (!zipWriter) {
        blobWriter = new BlobWriter("application/zip");
        zipWriter = new ZipWriter(blobWriter, {
          zip64: true,
        });
      }

      for (const item of uploads) {
        setArchiveCurrentFile(item.originalName);

        const response = await fetch(fileUrl(item), {
          cache: "no-store",
        });

        if (!response.ok || !response.body) {
          throw new Error(
            `Nie udało się pobrać pliku „${item.originalName}”.`,
          );
        }

        const completedBytesBeforeFile = completedBytes;
        const countedStream = createCountedStream(
          response.body,
          completedBytesBeforeFile,
          totalBytes,
          setArchiveProgress,
        );

        await zipWriter.add(
          createArchivePath(item, usedPaths),
          countedStream,
          {
            level: 0,
            lastModDate: new Date(item.uploadedAt),
          },
        );

        completedBytes += item.fileSize;
      }

      setArchiveCurrentFile("Finalizowanie archiwum…");

      const archiveResult = await zipWriter.close();

      setArchiveProgress(100);

      if (!saveDirectlyToDisk) {
        const archiveBlob =
          archiveResult instanceof Blob
            ? archiveResult
            : await blobWriter?.getData();

        if (!archiveBlob) {
          throw new Error(
            "Nie udało się utworzyć końcowego archiwum ZIP.",
          );
        }

        const archiveUrl = URL.createObjectURL(archiveBlob);
        const link = document.createElement("a");

        link.href = archiveUrl;
        link.download = "Wrzutka-weselna.zip";
        link.style.display = "none";

        document.body.appendChild(link);
        link.click();
        link.remove();

        window.setTimeout(() => {
          URL.revokeObjectURL(archiveUrl);
        }, 60_000);
      }
    } catch (error) {
      console.error("Could not create local ZIP archive:", error);

      if (zipWriter) {
        try {
          await zipWriter.close();
        } catch {
          // Writer mógł zostać już zamknięty po błędzie.
        }
      }

      setArchiveError(
        error instanceof Error
          ? error.message
          : "Nie udało się przygotować archiwum ZIP.",
      );
    } finally {
      setArchivePreparing(false);
      setArchiveCurrentFile("");
    }
  };

  const deletePreviewItem = async (): Promise<void> => {
    if (!previewItem || deleting) return;

    setDeleting(true);
    setDeleteError("");

    try {
      const response = await fetch(
        `/api/couple/delete/${encodeURIComponent(previewItem.id)}?token=${encodeURIComponent(token)}`,
        {
          method: "DELETE",
          headers: {
            Accept: "application/json",
          },
        },
      );

      const data = (await response.json()) as {
        result: number;
        message?: string;
      };

      if (!response.ok || data.result !== 0) {
        throw new Error(data.message || "Nie udało się usunąć pliku.");
      }

      const removedItem = previewItem;

      setUploads((currentUploads) =>
        currentUploads.filter((item) => item.id !== removedItem.id),
      );

      setStats((currentStats) => ({
        ...currentStats,
        totalFiles: Math.max(0, currentStats.totalFiles - 1),
        images: removedItem.mimeType.startsWith("image/")
          ? Math.max(0, currentStats.images - 1)
          : currentStats.images,
        videos: removedItem.mimeType.startsWith("video/")
          ? Math.max(0, currentStats.videos - 1)
          : currentStats.videos,
        other:
          !removedItem.mimeType.startsWith("image/") &&
          !removedItem.mimeType.startsWith("video/")
            ? Math.max(0, currentStats.other - 1)
            : currentStats.other,
        totalBytes: Math.max(
          0,
          currentStats.totalBytes - removedItem.fileSize,
        ),
      }));

      setDeleteDialogOpen(false);
      setPreviewItem(null);
    } catch (error) {
      console.error("Could not delete upload:", error);
      setDeleteError(
        error instanceof Error
          ? error.message
          : "Nie udało się usunąć pliku.",
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <ThemeProvider theme={galleryTheme}>
      <CssBaseline />

      <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 5 } }}>
        <Stack spacing={3}>
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              px: { xs: 2.5, sm: 5 },
              py: { xs: 4, sm: 5.5 },
              textAlign: "center",
              border: "1px solid rgba(199,163,74,.34)",
              background: `
                radial-gradient(circle at 50% 0%, rgba(199,163,74,.13), transparent 38%),
                linear-gradient(145deg, rgba(28,28,29,.99), rgba(7,7,8,.995))
              `,
              boxShadow: "0 24px 70px rgba(0,0,0,.36)",
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 11,
                border: "1px solid rgba(199,163,74,.14)",
                pointerEvents: "none",
              },
            }}
          >
            <Stack spacing={1.8} alignItems="center" sx={{ position: "relative" }}>
              <Typography
                variant="overline"
                sx={{
                  color: "primary.light",
                  letterSpacing: ".32em",
                  fontSize: ".68rem",
                }}
              >
                PRYWATNA GALERIA
              </Typography>

              <Typography
                variant="h1"
                sx={{
                  fontSize: "clamp(3.8rem, 14vw, 7rem)",
                  lineHeight: 0.85,
                  letterSpacing: "-.025em",
                  background:
                    "linear-gradient(180deg, #FFF4BF 0%, #E4C86F 30%, #C49A35 62%, #806018 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                W &amp; D
              </Typography>

              <Typography
                sx={{
                  fontFamily: '"Cormorant Garamond", Georgia, serif',
                  fontSize: { xs: "1.45rem", sm: "1.8rem" },
                  color: "text.primary",
                }}
              >
                Wspomnienia z naszego wyjątkowego dnia
              </Typography>

              <Typography
                color="text.secondary"
                sx={{ maxWidth: 650, lineHeight: 1.75 }}
              >
                Wszystkie zdjęcia i filmy przesłane przez naszych Gości w jednym,
                prywatnym miejscu.
              </Typography>
            </Stack>
          </Box>

          {!token && (
            <Alert severity="error">
              Brakuje tokenu dostępu. Otwórz pełny prywatny link otrzymany dla
              Pary Młodej.
            </Alert>
          )}

          {error && (
            <Alert
              severity="error"
              action={
                token ? (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => void loadUploads(true)}
                  >
                    Spróbuj ponownie
                  </Button>
                ) : undefined
              }
            >
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: "grid", placeItems: "center", py: 10 }}>
              <Stack spacing={2} alignItems="center">
                <CircularProgress />
                <Typography color="text.secondary">
                  Pobieranie wspomnień…
                </Typography>
              </Stack>
            </Box>
          ) : token && !error ? (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    sm: "repeat(4, minmax(0, 1fr))",
                  },
                  gap: 1.5,
                }}
              >
                <StatCard
                  icon={<InsertDriveFileOutlinedIcon />}
                  label="Wszystkie pliki"
                  value={stats.totalFiles}
                />
                <StatCard
                  icon={<PhotoLibraryOutlinedIcon />}
                  label="Zdjęcia"
                  value={stats.images}
                />
                <StatCard
                  icon={<VideocamOutlinedIcon />}
                  label="Filmy"
                  value={stats.videos}
                />
                <StatCard
                  icon={<StorageOutlinedIcon />}
                  label="Łączny rozmiar"
                  value={formatBytes(stats.totalBytes)}
                />
              </Box>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <ToggleButtonGroup
                  exclusive
                  value={filter}
                  onChange={(_, value: FilterValue | null) => {
                    if (value) setFilter(value);
                  }}
                  size="small"
                  color="primary"
                  sx={{
                    alignSelf: { xs: "stretch", sm: "auto" },
                    "& .MuiToggleButton-root": {
                      flex: { xs: 1, sm: "initial" },
                      borderColor: "rgba(199,163,74,.3)",
                    },
                  }}
                >
                  <ToggleButton value="all">
                    Wszystkie ({stats.totalFiles})
                  </ToggleButton>
                  <ToggleButton value="images">
                    Zdjęcia ({stats.images})
                  </ToggleButton>
                  <ToggleButton value="videos">
                    Filmy ({stats.videos})
                  </ToggleButton>
                </ToggleButtonGroup>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                >
                  <Button
                    variant="contained"
                    startIcon={
                      archivePreparing ? (
                        <CircularProgress
                          size={17}
                          color="inherit"
                        />
                      ) : (
                        <DownloadOutlinedIcon />
                      )
                    }
                    disabled={
                      !stats.totalFiles || archivePreparing
                    }
                    onClick={() => void downloadAllAsZip()}
                    sx={{
                      whiteSpace: "nowrap",
                      color: "#0B0B0C",
                    }}
                  >
                    {archivePreparing
                      ? `Przygotowywanie ${archiveProgress}%`
                      : "Pobierz wszystko"}
                  </Button>

                  <Button
                    variant="outlined"
                    startIcon={
                      refreshing ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <RefreshOutlinedIcon />
                      )
                    }
                    disabled={refreshing}
                    onClick={() => void loadUploads(true)}
                  >
                    Odśwież
                  </Button>
                </Stack>
              </Stack>

              {(archivePreparing || archiveError) && (
                <Card>
                  <CardContent
                    sx={{
                      p: 2,
                      "&:last-child": { pb: 2 },
                    }}
                  >
                    {archivePreparing && (
                      <Stack spacing={1.4}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          spacing={2}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography fontWeight={700}>
                              Przygotowywanie archiwum ZIP
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              noWrap
                              title={archiveCurrentFile}
                            >
                              {archiveCurrentFile ||
                                "Pobieranie plików…"}
                            </Typography>
                          </Box>
                          <Typography
                            color="primary.light"
                            fontWeight={700}
                          >
                            {archiveProgress}%
                          </Typography>
                        </Stack>

                        <Box
                          sx={{
                            height: 8,
                            overflow: "hidden",
                            bgcolor: "rgba(255,255,255,.08)",
                            borderRadius: 999,
                          }}
                        >
                          <Box
                            sx={{
                              width: `${archiveProgress}%`,
                              height: "100%",
                              borderRadius: 999,
                              background:
                                "linear-gradient(90deg, #A98325, #E7CE7D)",
                              boxShadow:
                                "0 0 16px rgba(226,199,119,.35)",
                              transition: "width .18s ease",
                            }}
                          />
                        </Box>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          Nie zamykaj tej karty. Archiwum jest
                          przygotowywane na tym urządzeniu.
                        </Typography>
                      </Stack>
                    )}

                    {archiveError && !archivePreparing && (
                      <Alert
                        severity="error"
                        onClose={() => setArchiveError("")}
                      >
                        {archiveError}
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              )}

              {filteredUploads.length === 0 ? (
                <Card>
                  <CardContent sx={{ py: 8, textAlign: "center" }}>
                    <Typography
                      sx={{
                        fontFamily: '"Cormorant Garamond", Georgia, serif',
                        fontSize: "1.7rem",
                      }}
                    >
                      Nie ma jeszcze plików w tej kategorii
                    </Typography>
                    <Typography color="text.secondary">
                      Nowe wspomnienia pojawią się tutaj po odświeżeniu.
                    </Typography>
                  </CardContent>
                </Card>
              ) : (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(2, minmax(0, 1fr))",
                      sm: "repeat(3, minmax(0, 1fr))",
                      md: "repeat(4, minmax(0, 1fr))",
                    },
                    gap: { xs: 1.2, sm: 1.8 },
                  }}
                >
                  {filteredUploads.map((item) => {
                    const isImage = item.mimeType.startsWith("image/");
                    const isVideo = item.mimeType.startsWith("video/");
                    const src = fileUrl(item);

                    return (
                      <Card
                        key={item.id}
                        sx={{
                          minWidth: 0,
                          overflow: "hidden",
                          transition:
                            "transform .18s ease, border-color .18s ease, box-shadow .18s ease",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            borderColor: "rgba(226,199,119,.7)",
                            boxShadow: "0 18px 42px rgba(0,0,0,.38)",
                          },
                        }}
                      >
                        <Box
                          role="button"
                          tabIndex={0}
                          aria-label={`Otwórz podgląd: ${item.originalName}`}
                          onClick={() => setPreviewItem(item)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              setPreviewItem(item);
                            }
                          }}
                          sx={{
                            position: "relative",
                            aspectRatio: "1 / 1",
                            overflow: "hidden",
                            display: "grid",
                            placeItems: "center",
                            cursor: "pointer",
                            backgroundColor: "#0D0D0E",
                          }}
                        >
                          {isImage ? (
                            <Box
                              component="img"
                              src={src}
                              alt={item.originalName}
                              loading="lazy"
                              sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                transition: "transform .25s ease",
                                ".MuiCard-root:hover &": {
                                  transform: "scale(1.025)",
                                },
                              }}
                            />
                          ) : isVideo ? (
                            <>
                              <Box
                                component="video"
                                src={src}
                                preload="metadata"
                                muted
                                playsInline
                                sx={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  pointerEvents: "none",
                                }}
                              />
                              <Box
                                sx={{
                                  position: "absolute",
                                  inset: 0,
                                  display: "grid",
                                  placeItems: "center",
                                  backgroundColor: "rgba(0,0,0,.22)",
                                }}
                              >
                                <PlayCircleOutlineIcon
                                  sx={{
                                    fontSize: 56,
                                    color: "primary.light",
                                    filter: "drop-shadow(0 4px 12px rgba(0,0,0,.5))",
                                  }}
                                />
                              </Box>
                            </>
                          ) : (
                            <InsertDriveFileOutlinedIcon
                              sx={{ fontSize: 60, color: "primary.light" }}
                            />
                          )}

                          <Chip
                            size="small"
                            label={isImage ? "Zdjęcie" : isVideo ? "Film" : "Plik"}
                            sx={{
                              position: "absolute",
                              top: 9,
                              right: 9,
                              color: "#0B0B0C",
                              bgcolor: "rgba(226,199,119,.92)",
                              fontWeight: 700,
                            }}
                          />
                        </Box>

                        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                          <Typography
                            variant="body2"
                            noWrap
                            title={item.originalName}
                            sx={{ fontWeight: 650 }}
                          >
                            {item.originalName}
                          </Typography>

                          <Typography
                            variant="caption"
                            color="primary.light"
                            noWrap
                            sx={{ display: "block", mt: 0.35 }}
                          >
                            {item.guestName}
                          </Typography>

                          <Stack
                            direction="row"
                            spacing={0.8}
                            justifyContent="space-between"
                            sx={{ mt: 0.8 }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(item.uploadedAt)}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ flexShrink: 0 }}
                            >
                              {formatBytes(item.fileSize)}
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              )}
            </>
          ) : null}
        </Stack>
      </Container>

      <Dialog
        open={Boolean(previewItem)}
        onClose={() => setPreviewItem(null)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle sx={{ pr: 7, position: "relative" }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="span"
              noWrap
              title={previewItem?.originalName}
              sx={{ display: "block", fontWeight: 700 }}
            >
              {previewItem?.originalName}
            </Typography>
            {previewItem && (
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
                sx={{ display: "block" }}
              >
                {previewItem.guestName} · {formatDate(previewItem.uploadedAt)}
              </Typography>
            )}
          </Box>

          <IconButton
            aria-label="Zamknij podgląd"
            onClick={() => setPreviewItem(null)}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              color: "primary.light",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            p: 0,
            minHeight: 260,
            display: "grid",
            placeItems: "center",
            backgroundColor: "#050505",
          }}
        >
          {previewItem?.mimeType.startsWith("image/") && (
            <Box
              component="img"
              src={fileUrl(previewItem)}
              alt={previewItem.originalName}
              sx={{
                display: "block",
                maxWidth: "100%",
                maxHeight: "78vh",
                objectFit: "contain",
              }}
            />
          )}

          {previewItem?.mimeType.startsWith("video/") && (
            <Box
              component="video"
              key={previewItem.id}
              src={fileUrl(previewItem)}
              controls
              autoPlay
              playsInline
              sx={{
                display: "block",
                width: "100%",
                maxHeight: "78vh",
                backgroundColor: "#050505",
              }}
            />
          )}
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Button
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => {
              setDeleteError("");
              setDeleteDialogOpen(true);
            }}
            disabled={!previewItem}
          >
            Usuń ten plik
          </Button>

          <Stack direction="row" spacing={1}>
            {previewItem && (
              <Tooltip title="Otwórz plik w nowej karcie">
                <Button
                  component="a"
                  href={fileUrl(previewItem)}
                  target="_blank"
                  rel="noreferrer"
                  startIcon={<OpenInNewIcon />}
                >
                  Otwórz osobno
                </Button>
              </Tooltip>
            )}
            <Button variant="contained" onClick={() => setPreviewItem(null)}>
              Zamknij
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (!deleting) {
            setDeleteDialogOpen(false);
            setDeleteError("");
          }
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Usunąć ten plik?</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="warning">
              Plik zniknie z galerii i pobieranego archiwum. Nadal pozostanie
              bezpiecznie zapisany w magazynie, aby można było go odzyskać.
            </Alert>

            {previewItem && (
              <Box>
                <Typography fontWeight={700}>
                  {previewItem.originalName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Dodał: {previewItem.guestName}
                </Typography>
              </Box>
            )}

            {deleteError && (
              <Alert severity="error">{deleteError}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setDeleteError("");
            }}
            disabled={deleting}
          >
            Anuluj
          </Button>
          <Button
            color="error"
            variant="contained"
            startIcon={
              deleting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DeleteOutlineIcon />
              )
            }
            onClick={() => void deletePreviewItem()}
            disabled={deleting || !previewItem}
          >
            {deleting ? "Usuwanie…" : "Tak, usuń plik"}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}
